




















































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { MiraInsurer, UnresolvedInsurerDecision, UnresolvedInsurerGroup } from "@/data/models/MiraInsurerAdministration";
import { decideUnresolvedInsurers } from "@/data/repository/MiraInsurerAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { insurerErrorMessage } from "@/utils/MiraInsurerAdministration";

/** "Neuer Versicherer" (#2610125): nothing is sent yet; the caller creates the insurer with these spellings. */
export interface NewInsurerRequest {
  kind: "NEW_INSURER";
  texts: string[];
}

/** What `show()` resolves with. */
export type MiraInsurerDecisionResult = UnresolvedInsurerDecision | NewInsurerRequest;

/** A taken-over suggestion (#2610125): the choice selected and only its spellings ticked. */
export interface MiraInsurerDecisionPreselection {
  kind: "INSURER" | "NOT_INSURER";
  key: string | null;
  texts: string[];
}

/**
 * Decides one group of unresolved insurer names (Track #2610040 P3): the ticked spellings go to an
 * insurer, whose spellings they join, or are decided "Kein Versicherer". IRES Cloud refuses a spelling
 * another owner already claims (`decideUnresolvedInsurersForAdministration`). For an insurer missing
 * from the list (#2610125) the dialog sends nothing and hands the spellings back, so the caller opens the
 * insurer dialog and one save creates the insurer with them.
 *
 * `show()` resolves with the decision sent, the new-insurer request, or null when closed without one.
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class MiraInsurerDecisionDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  busy = false;
  spellings: Array<{ text: string; projects: number }> = [];
  selected: string[] = [];
  insurers: MiraInsurer[] = [];
  kind: "INSURER" | "NEW_INSURER" | "NOT_INSURER" = "INSURER";
  insurerKey: string | null = null;

  private resolve: ((result: MiraInsurerDecisionResult | null) => void) | null = null;

  get insurerItems(): Array<{ key: string; label: string }> {
    return this.insurers
      .map((insurer) => ({ key: insurer.key, label: `${insurer.shortName} (${insurer.code})` }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }

  get canApply(): boolean {
    // Read both first, so the computed tracks them whichever branch runs (ui-conventions.md, Reactivity).
    const selected = this.selected.length > 0;
    const insurer = this.insurerKey !== null;
    return selected && (this.kind !== "INSURER" || insurer);
  }

  get decision(): MiraInsurerDecisionResult | null {
    if (!this.canApply) return null;
    if (this.kind === "NEW_INSURER") return { kind: "NEW_INSURER", texts: this.selectedInOrder() };
    return this.kind === "NOT_INSURER"
      ? { kind: "NOT_INSURER", texts: [...this.selected] }
      : { kind: "INSURER", key: this.insurerKey as string, texts: [...this.selected] };
  }

  /** The ticked spellings in the group's order, the most used first: the first one names the new insurer. */
  private selectedInOrder(): string[] {
    return this.spellings.map((spelling) => spelling.text).filter((text) => this.selected.includes(text));
  }

  get effectText(): string | null {
    const insurer = this.insurers.find((entry) => entry.key === this.insurerKey);
    // A new insurer's own dialog says what its save reaches.
    if (this.kind === "NEW_INSURER") return null;
    if (this.kind === "NOT_INSURER") return this.$t("admin.miraInsurerReview.decision.effectNotInsurer").toString();
    return insurer ? this.$t("admin.miraInsurerReview.decision.effectInsurer", [insurer.shortName]).toString() : null;
  }

  /**
   * @param group - the group to decide.
   * @param insurers - the active insurers to choose from.
   * @param preselection - a taken-over suggestion, or null for an open choice with every spelling ticked.
   */
  show(
    group: UnresolvedInsurerGroup,
    insurers: MiraInsurer[],
    preselection: MiraInsurerDecisionPreselection | null = null
  ): Promise<MiraInsurerDecisionResult | null> {
    this.spellings = [...group.spellings];
    const texts = group.spellings.map((spelling) => spelling.text);
    this.selected = preselection ? texts.filter((text) => preselection.texts.includes(text)) : texts;
    this.insurers = insurers;
    this.kind = preselection?.kind ?? "INSURER";
    this.insurerKey = preselection?.kind === "INSURER" ? preselection.key : null;
    this.baseDialog.show({
      title: this.$t("admin.miraInsurerReview.decision.title").toString(),
      positiveButtonText: this.$t("admin.miraInsurerReview.decision.apply").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** WEITER for a new insurer, which is created in the next dialog; ZUORDNEN otherwise. */
  @Watch("kind")
  onKindChanged(kind: string) {
    this.baseDialog.positiveButtonText = this.$t(
      kind === "NEW_INSURER" ? "admin.miraInsurerReview.decision.next" : "admin.miraInsurerReview.decision.apply"
    ).toString();
  }

  async onApply() {
    const decision = this.decision;
    if (!decision) return;
    if (decision.kind === "NEW_INSURER") {
      this.baseDialog.isShowing = false;
      this.finish(decision);
      return;
    }
    this.busy = true;
    try {
      await decideUnresolvedInsurers(decision);
      SnackbarModule.showSuccess({ key: "admin.miraInsurerReview.decided" });
      this.baseDialog.isShowing = false;
      this.finish(decision);
    } catch (error) {
      SnackbarModule.showError(insurerErrorMessage(error));
    } finally {
      this.busy = false;
    }
  }

  finish(result: MiraInsurerDecisionResult | null) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
